import type { AgencySignOn, Industries } from 'apps/agency/agencyInterfaces'
import {
  AGENCY_LEVELS,
  AGENCY_LEVELS_LABELS,
  EMAIL_VALUES,
  STEPS_BY_LEVEL,
} from 'apps/agency/model/constants'
import type { AgencyLevel, ParsedAgencyAchievements } from 'apps/agency/model/types'

const getAgency = (state: RootState) => state.agency

export const getAgencySignOn = (state: RootState): AgencySignOn => getAgency(state).signon

export const getAgencyStatus = (state: RootState) => getAgency(state).status

export const getAgencyEmailConfirmation = (state: RootState) =>
  getAgency(state).email.emailConfirmation

export const getAgencyCommissions = (state: RootState) => getAgency(state).commissions

export const getAgencyCommissionsFilters = (state: RootState) => getAgencyCommissions(state).filters

export const getHasAgencyPage = (state: RootState): boolean =>
  Boolean(getAgencySignOn(state).agency_id)

export const getSignOnAgencyId = (state: RootState): number | null =>
  getAgencySignOn(state)?.agency_id ?? null

export const isAgencyProgressExist = (state: RootState): boolean => {
  const { agency } = state
  const { status } = agency
  const { achievements } = status

  return Boolean(
    getSignOnAgencyId(state) &&
      status.currentStatus &&
      status.currentStatus !== AGENCY_LEVELS.expert &&
      achievements &&
      Object.keys(achievements).length,
  )
}
export const getAgencyNextStatusProgress = (state: RootState) => {
  if (!isAgencyProgressExist(state)) {
    return 0
  }

  const { agency } = state
  const { status } = agency
  const { achievements, currentStatus } = status
  if (!achievements) {
    return 0
  }

  if (!currentStatus) {
    return 0
  }

  const currentSteps = STEPS_BY_LEVEL[currentStatus] as
    | Array<keyof ParsedAgencyAchievements>
    | undefined
  if (!currentSteps) {
    return 0
  }

  return (
    currentSteps
      .map((step) => {
        const achievement = achievements[step]

        return typeof achievement === 'object' ? achievement.passed : achievement
      })
      .reduce((prev, next) => Number(prev) + Number(next), 0) / currentSteps.length
  )
}

export const getAgencyNextStatus = (state: RootState): AgencyLevel | null => {
  const { agency } = state
  const { status } = agency
  const { currentStatus } = status

  if (currentStatus === AGENCY_LEVELS.applicant) {
    return AGENCY_LEVELS.partner
  }
  if (currentStatus === AGENCY_LEVELS.partner) {
    return AGENCY_LEVELS.expert
  }

  if (!currentStatus) {
    return null
  }

  return currentStatus
}

export const getAgencyProgressData = (state: RootState) => {
  if (isAgencyProgressExist(state)) {
    const nextStatus = getAgencyNextStatus(state)
    if (!nextStatus) {
      return null
    }

    return {
      progress: getAgencyNextStatusProgress(state),
      nextStatus,
      nextStatusLabel: AGENCY_LEVELS_LABELS[nextStatus],
    }
  }

  return null
}

const EMPTY_INDUSTRIES: Industries = Object.freeze({})

export const getFilterIndustries = (state: RootState): Industries =>
  getAgency(state).listing?.filtersData?.industries ?? EMPTY_INDUSTRIES

export const getAgencyProfileEmail = (state: RootState): string | null => {
  const signon = getAgencySignOn(state)
  const user = state.app.user

  return signon.email_type === EMAIL_VALUES.agency && signon.email
    ? signon.email
    : user.email || user.email_unverified
}

export const getShowAgencyMacColumn = (state: RootState): boolean =>
  Boolean(getSignOnAgencyId(state)) &&
  Boolean(getAgencyStatus(state).currentStatus) &&
  getAgencyStatus(state).currentStatus !== AGENCY_LEVELS.applicant
